// src/auth/firebase.js
//
// Phase 2 Sprint 1: Firebase app initialization with explicit auth mode (P2 tri-state).
// Phase 2 Sprint 2: Firestore added (tri-state aware; mock skips entirely per P7).
// WP-3: Cloud Functions client added (region asia-east2, locked #11; emulator port 5001).
//
// VITE_AUTH_MODE (required env var):
//   - mock     = Phase 1 localStorage mock auth fallback (dev only)
//   - emulator = Auth + Firestore + Functions Emulators (dev/CI; all MUST be running)
//   - firebase = Real Firebase config (production-like; full Firebase config MUST be present)
//
// Each mode has different runtime requirements. NO silent fallback between modes.
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || 'mock';
// Cloud Functions region (CLAUDE.md V4 locked #11).
const FUNCTIONS_REGION = 'asia-east2';
// Validate mode (P2 explicit)
if (!['mock', 'emulator', 'firebase'].includes(AUTH_MODE)) {
    throw new Error(
        `Invalid VITE_AUTH_MODE: "${AUTH_MODE}". ` +
        'Must be one of: mock, emulator, firebase. ' +
        'See docs/firebase-emulator-setup.md for setup instructions.'
    );
}
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
let auth = null;
let db = null;
let functions = null;
if (AUTH_MODE === 'firebase' || AUTH_MODE === 'emulator') {
    // P2: emulator + firebase modes both require firebaseConfig present
    const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];
    const missingKeys = requiredKeys.filter(k => !firebaseConfig[k]);
    if (missingKeys.length > 0) {
        throw new Error(
            `VITE_AUTH_MODE="${AUTH_MODE}" requires Firebase config but missing keys: ${missingKeys.join(', ')}. ` +
            'Check .env.local against .env.example template. ' +
            'For mock-mode development, set VITE_AUTH_MODE=mock.'
        );
    }
    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app, FUNCTIONS_REGION);
    if (AUTH_MODE === 'emulator') {
        // P2: emulator mode REQUIRES Auth Emulator at localhost:9099 - no silent fallback
        try {
            connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
            console.info('Firebase Auth Emulator connected at localhost:9099');
        } catch (err) {
            throw new Error(
                'VITE_AUTH_MODE="emulator" but Auth Emulator unavailable at localhost:9099. ' +
                'Start with: npx firebase emulators:start --only auth,firestore,functions. ' +
                'See docs/firebase-emulator-setup.md.'
            );
        }
        // Sprint 2: Firestore Emulator integration - mirrors Auth Emulator pattern
        try {
            connectFirestoreEmulator(db, 'localhost', 8080);
            console.info('Firestore Emulator connected at localhost:8080');
        } catch (err) {
            throw new Error(
                'VITE_AUTH_MODE="emulator" but Firestore Emulator unavailable at localhost:8080. ' +
                'Start with: npx firebase emulators:start --only auth,firestore,functions. ' +
                'See docs/firebase-emulator-setup.md.'
            );
        }
        // WP-3: Functions Emulator (port 5001 per firebase.json)
        try {
            connectFunctionsEmulator(functions, 'localhost', 5001);
            console.info('Firebase Functions Emulator connected at localhost:5001');
        } catch (err) {
            throw new Error(
                'VITE_AUTH_MODE="emulator" but Functions Emulator unavailable at localhost:5001. ' +
                'Start with: npx firebase emulators:start --only auth,firestore,functions.'
            );
        }
    }
}
// mock mode: auth, db, functions all remain null; callers MUST branch on AUTH_MODE (P7)
export { auth, db, functions, AUTH_MODE, FUNCTIONS_REGION };
